import { useLazyQuery } from '@apollo/client/react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { map } from 'lodash';
import { debounce } from 'lodash/fp';
import React, { useRef } from 'react';

import EssealPhoneInput from '@/components/EssealPhoneInput';
import { type CompaniesHouseInfo, GetCompaniesHouseInfoDocument } from '@/graphql/generated/graphql';
import { COMPANY_TYPE_OPTIONS, type DEFAULT_COMPANY_INFO } from '@/utils/constants';
import { CITIES, REGIONS } from '@/utils/UK';
import { isValidEmail } from '@/utils/userInformation';

interface CompanyDetailsFieldsProps {
  companyInfo: typeof DEFAULT_COMPANY_INFO;
  onFieldChange: (key: string, value: string) => void;
  selectedState: string | null;
  onSelectedStateChange: (value: string | null) => void;
  selectedCity: string | null;
  onSelectedCityChange: (value: string | null) => void;
  handleAutofill: (companiesHouseInfo: CompaniesHouseInfo) => void;
}

const normalDebounce = debounce(500);

const CompanyDetailsFields: React.FC<CompanyDetailsFieldsProps> = ({
  companyInfo,
  onFieldChange,
  selectedState,
  onSelectedStateChange,
  selectedCity,
  onSelectedCityChange,
  handleAutofill,
}) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [getCompanyInfo, { data, loading }] = useLazyQuery(GetCompaniesHouseInfoDocument, {
    fetchPolicy: 'network-only',
  });

  const onChangeCompanyNumber = (value: string) => {
    onFieldChange('companyNumber', value);

    if (value.trim()) {
      debounceFunc.cancel();
      debounceFunc(() => {
        getCompanyInfo({
          variables: { companyNumber: value },
        });
      });
    }
  };

  return (
    <>
      <FormControl sx={{ flex: 1, minWidth: 140, alignItems: 'baseline' }}>
        <FormLabel htmlFor="company-number" sx={{ fontSize: 12, mb: 0.5 }}>
          Company Number
        </FormLabel>
        <TextField
          required
          fullWidth
          size="small"
          id="company-number"
          variant="outlined"
          value={companyInfo.companyNumber}
          placeholder="e.g. 12345678"
          onChange={(e) => onChangeCompanyNumber(e.target.value)}
          error={companyInfo.companyNumber.trim() === ''}
          helperText={companyInfo.companyNumber.trim() === '' ? 'Company Number is required' : ''}
        />
        {loading && <Typography variant="caption">Loading data...</Typography>}
        {!loading && companyInfo.companyNumber?.trim() && data?.getCompaniesHouseInfo && (
          <Button size="small" onClick={() => handleAutofill(data?.getCompaniesHouseInfo)}>
            Autofill Company Data
          </Button>
        )}
      </FormControl>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ flex: 1, minWidth: 140, alignItems: 'baseline' }}>
          <FormLabel htmlFor="company-name" sx={{ fontSize: 12, mb: 0.5 }}>
            Company Name
          </FormLabel>
          <TextField
            required
            fullWidth
            size="small"
            id="company-name"
            variant="outlined"
            value={companyInfo.name}
            placeholder="Enter your company name"
            onChange={(e) => onFieldChange('name', e.target.value)}
            error={companyInfo.name.trim() === ''}
            helperText={companyInfo.name.trim() === '' ? 'Company Name is required' : ''}
          />
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 140 }}>
          <FormLabel htmlFor="trading-name" sx={{ fontSize: 12, mb: 0.5 }}>
            Trading Name
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            id="trading-name"
            variant="outlined"
            value={companyInfo.tradingName}
            placeholder="If different from company name"
            onChange={(e) => onFieldChange('tradingName', e.target.value)}
          />
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ flex: 1, minWidth: 140 }}>
          <FormLabel htmlFor="vat-number" sx={{ fontSize: 12, mb: 0.5 }}>
            VAT Number
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            id="vat-number"
            variant="outlined"
            value={companyInfo.vatNumber}
            placeholder="e.g. GB123456789"
            onChange={(e) => onFieldChange('vatNumber', e.target.value)}
          />
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 280 }}>
          <FormLabel htmlFor="company-type" sx={{ fontSize: 12, mb: 0.5 }}>
            Company Type
          </FormLabel>
          <Select
            size="small"
            id="company-type"
            value={companyInfo.companyType}
            onChange={(e) => onFieldChange('companyType', e.target.value)}
          >
            {map(COMPANY_TYPE_OPTIONS, (opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FormControl>
        <FormLabel htmlFor="company-phone" sx={{ fontSize: 12, mb: 0.5 }}>
          Primary Phone
        </FormLabel>
        <EssealPhoneInput
          id="company-phone"
          defaultCountry="GB"
          style={{ width: '100%' }}
          key={`phone-${companyInfo.id}`}
          value={companyInfo.phone}
          placeholder="Enter your company phone"
          onChange={(e164) => onFieldChange('phone', e164)}
          required
          error={companyInfo.phone.trim() === '' ? 'Phone is required' : ''}
        />
      </FormControl>
      <FormControl>
        <FormLabel htmlFor="company-email" sx={{ fontSize: 12, mb: 0.5 }}>
          Primary Email
        </FormLabel>
        <TextField
          required
          fullWidth
          type="email"
          size="small"
          variant="outlined"
          id="company-email"
          value={companyInfo.email}
          placeholder="Enter your company email"
          onChange={(e) => onFieldChange('email', e.target.value)}
          error={companyInfo.email.trim() === '' || !isValidEmail(companyInfo.email)}
          helperText={
            companyInfo.email.trim() === ''
              ? 'Email is required.'
              : !isValidEmail(companyInfo.email)
                ? 'Invalid Email'
                : ''
          }
        />
      </FormControl>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Address
      </Typography>
      <FormControl fullWidth>
        <FormLabel htmlFor="street-address" sx={{ fontSize: 12, mb: 0.5 }}>
          Street Address
        </FormLabel>
        <TextField
          id="street-address"
          placeholder="Street Address"
          value={companyInfo.street}
          fullWidth
          variant="outlined"
          size="small"
          required
          onChange={(e) => onFieldChange('street', e.target.value)}
          error={companyInfo.street?.trim() === ''}
          helperText={companyInfo.street?.trim() === '' ? 'Street is required' : ''}
        />
      </FormControl>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Country</FormLabel>
          <TextField value="United Kingdom" disabled fullWidth variant="outlined" size="small" />
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>State / Province</FormLabel>
          <Autocomplete
            options={REGIONS}
            value={selectedState}
            onChange={(_, v) => onSelectedStateChange(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="State / Province"
                error={selectedState === null}
                helperText={selectedState === null ? 'State is required' : ''}
              />
            )}
          />
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>City</FormLabel>
          <Autocomplete
            options={selectedState ? (CITIES[selectedState as keyof typeof CITIES] ?? []) : []}
            value={selectedCity}
            disabled={!selectedState}
            onChange={(_, v) => onSelectedCityChange(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="City"
                error={selectedCity === null}
                helperText={selectedCity === null ? 'City is required' : ''}
              />
            )}
          />
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 120 }}>
          <FormLabel htmlFor="zip" sx={{ fontSize: 12, mb: 0.5 }}>
            Zip / Postal code
          </FormLabel>
          <TextField
            id="zip"
            placeholder="Zip / Postal code"
            value={companyInfo.zip}
            fullWidth
            variant="outlined"
            size="small"
            required
            onChange={(e) => onFieldChange('zip', e.target.value)}
            error={companyInfo.zip?.trim() === ''}
            helperText={companyInfo.zip?.trim() === '' ? 'Zip is required' : ''}
          />
        </FormControl>
      </Box>
    </>
  );
};

export default CompanyDetailsFields;
